// RegionContext — tracks which region the super-admin is currently scoping
// their dispatcher view to. Regional dispatchers don't see the switcher;
// their region is locked to user.region from auth.
//
// Usage:
//   import { useRegionScope } from './RegionContext';
//   const { selectedRegion, setSelectedRegion, regions, isAll } = useRegionScope();
//
// `selectedRegion` is "all" when the super-admin wants cross-region view, or
// a specific region code (e.g. "dmv", "nj"). For non-super-admins, it always
// equals the user's own region.

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const RegionContext = createContext({
  selectedRegion: 'all',
  setSelectedRegion: () => {},
  regions: [],
  isAll: true,
  isSuperAdmin: false,
  loading: false,
});

const STORAGE_KEY = 'ashandmerch:selected_region';

export function RegionProvider({ children }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // Initialise from localStorage for super-admin; lock to user.region otherwise
  const [selectedRegion, setSelectedRegionState] = useState(() => {
    if (!user) return 'all';
    if (user.role === 'super_admin') {
      try { return localStorage.getItem(STORAGE_KEY) || 'all'; } catch { return 'all'; }
    }
    return user.region || 'all';
  });

  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Whenever the user object changes, recompute the lock:
  useEffect(() => {
    if (!user) {
      setSelectedRegionState('all');
      return;
    }
    if (user.role === 'super_admin') {
      try { setSelectedRegionState(localStorage.getItem(STORAGE_KEY) || 'all'); } catch { setSelectedRegionState('all'); }
    } else {
      setSelectedRegionState(user.region || 'all');
    }
  }, [user?.id, user?.role, user?.region]);

  // Fetch the operator-visible regions list (only dispatcher / super-admin view)
  useEffect(() => {
    if (!user || !['dispatcher', 'super_admin'].includes(user.role)) {
      setRegions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/regions`, { withCredentials: true })
      .then(res => { if (!cancelled) setRegions(res.data?.regions || []); })
      .catch(() => { if (!cancelled) setRegions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  const setSelectedRegion = useCallback((region) => {
    if (!isSuperAdmin) return; // regional users can't change their scope
    setSelectedRegionState(region);
    try {
      if (region === 'all') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, region);
    } catch {/* ignore */}
  }, [isSuperAdmin]);

  return (
    <RegionContext.Provider
      value={{
        selectedRegion,
        setSelectedRegion,
        regions,
        isAll: selectedRegion === 'all',
        isSuperAdmin,
        loading,
      }}
    >
      {children}
    </RegionContext.Provider>
  );
}

export function useRegionScope() {
  return useContext(RegionContext);
}

// Helper for client-side filtering of region-tagged records by selectedRegion.
// Pass an array of {region}-having records, returns the subset matching the
// scope. "all" passes everything through; non-super-admin's lock is enforced
// by the backend so this is pure UX convenience.
export function filterByRegionScope(items, selectedRegion) {
  if (!Array.isArray(items)) return items;
  if (!selectedRegion || selectedRegion === 'all') return items;
  return items.filter((it) => (it?.region || null) === selectedRegion);
}
